import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { fetchAllJobs, deleteJob } from "../../apis/JobApis";
import { useAuth } from "../Authentication/AuthContext";
import { useToastHelpers } from "../Toasts/ToastUtils";
import { SkeletonAdminTable } from "../Skeletons";
import EditJobModal from "./EditJobModal";

export default function AdminJobsTab() {
  const { user } = useAuth();
  const { successToast, errorToast } = useToastHelpers();

  // Data states
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [activeItems, setActiveItems] = useState(0);
  const ITEMS_PER_PAGE = 15;

  // Filter states
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("job"); // Default to job
  const [statusFilter, setStatusFilter] = useState(""); // empty = all, "active", "expired"

  // Sorting states
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  // Action states
  const [editingJob, setEditingJob] = useState(null);
  const [deletingJobId, setDeletingJobId] = useState(null);

  // Stats for all types
  const [stats, setStats] = useState({
    jobs: { total: 0, active: 0 },
    internships: { total: 0, active: 0 },
    referrals: { total: 0, active: 0 },
  });

  // Load stats for all types on mount
  useEffect(() => {
    const loadStats = async () => {
      try {
        const [jobsData, internshipsData, referralsData] = await Promise.all([
          fetchAllJobs({ page: 1, size: 1, type: "job" }),
          fetchAllJobs({ page: 1, size: 1, type: "internship" }),
          fetchAllJobs({ page: 1, size: 1, type: "referral" }),
        ]);

        setStats({
          jobs: {
            total: jobsData?.total || 0,
            active: jobsData?.active_jobs || 0,
          },
          internships: {
            total: internshipsData?.total || 0,
            active: internshipsData?.active_jobs || 0,
          },
          referrals: {
            total: referralsData?.total || 0,
            active: referralsData?.active_jobs || 0,
          },
        });
      } catch (error) {
        console.error("Error loading stats:", error);
      }
    };

    loadStats();
  }, []);

  // Load jobs with pagination
  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        size: ITEMS_PER_PAGE,
        type: typeFilter || undefined,
        status: statusFilter || undefined,
        keyword: search || undefined,
      };

      const data = await fetchAllJobs(params);
      const jobsList = Array.isArray(data) ? data : data?.jobs || [];

      setJobs(jobsList);
      setTotalItems(data?.total || jobsList.length);
      setActiveItems(data?.active_jobs || 0);
    } catch (error) {
      errorToast("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  }, [currentPage, typeFilter, statusFilter, search, errorToast]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // Determine if job is expired
  const isExpired = (job) => {
    if (!job.expiryDate) return false;
    return new Date(job.expiryDate) < new Date();
  };

  // Client-side search filter (for quick filtering within current page)
  const searchFilteredJobs = useMemo(() => {
    if (!search) return jobs;
    const lowerSearch = search.toLowerCase();
    return jobs.filter(
      (job) =>
        job.title?.toLowerCase().includes(lowerSearch) ||
        job.companyName?.toLowerCase().includes(lowerSearch)
    );
  }, [jobs, search]);

  // Sort jobs - expired at end, then by sortField
  const sortedJobs = useMemo(() => {
    const sorted = [...searchFilteredJobs].sort((a, b) => {
      // First, put expired jobs at the end
      const aExpired = isExpired(a);
      const bExpired = isExpired(b);
      if (aExpired !== bExpired) {
        return aExpired ? 1 : -1;
      }

      // Then sort by the selected field
      let aVal, bVal;

      switch (sortField) {
        case "title":
          aVal = a.title?.toLowerCase() || "";
          bVal = b.title?.toLowerCase() || "";
          break;
        case "companyName":
          aVal = a.companyName?.toLowerCase() || "";
          bVal = b.companyName?.toLowerCase() || "";
          break;
        case "type":
          aVal = a.type?.toLowerCase() || "";
          bVal = b.type?.toLowerCase() || "";
          break;
        case "expiryDate":
          aVal = a.expiryDate ? new Date(a.expiryDate).getTime() : 0;
          bVal = b.expiryDate ? new Date(b.expiryDate).getTime() : 0;
          break;
        case "likes":
          aVal = a.likes ? Object.keys(a.likes).length : 0;
          bVal = b.likes ? Object.keys(b.likes).length : 0;
          break;
        case "applies":
          aVal = a.applies ? Object.keys(a.applies).length : 0;
          bVal = b.applies ? Object.keys(b.applies).length : 0;
          break;
        case "createdAt":
        default:
          aVal = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bVal = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
      }

      if (sortOrder === "asc") {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });

    return sorted;
  }, [searchFilteredJobs, sortField, sortOrder]);

  // Pagination calculations
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  // Handle sort click
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // Sort indicator component
  const SortIndicator = ({ field }) => {
    if (sortField !== field) {
      return <i className="ri-arrow-up-down-line text-gray-400 ml-1"></i>;
    }
    return sortOrder === "asc" ? (
      <i className="ri-arrow-up-s-line text-blue-600 ml-1"></i>
    ) : (
      <i className="ri-arrow-down-s-line text-blue-600 ml-1"></i>
    );
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm("Are you sure you want to delete this job?")) return;

    setDeletingJobId(jobId);
    try {
      await deleteJob(jobId, user.token);
      successToast("Job deleted successfully");
      loadJobs(); // Reload to get updated data
    } catch (error) {
      errorToast("Failed to delete job");
    } finally {
      setDeletingJobId(null);
    }
  };

  const handleEditSuccess = (updatedJob) => {
    setJobs((prev) =>
      prev.map((j) => (j.id === updatedJob.id ? updatedJob : j))
    );
    setEditingJob(null);
    successToast("Job updated successfully");
  };

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setCurrentPage(1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Get stats for selected type
  const getCurrentTypeStats = () => {
    switch (typeFilter) {
      case "job":
        return stats.jobs;
      case "internship":
        return stats.internships;
      case "referral":
        return stats.referrals;
      default:
        return { total: 0, active: 0 };
    }
  };

  const currentStats = getCurrentTypeStats();

  return (
    <div>
      {/* Type Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { key: "job", label: "Jobs", icon: "ri-briefcase-line", stats: stats.jobs },
          { key: "internship", label: "Internships", icon: "ri-graduation-cap-line", stats: stats.internships },
          { key: "referral", label: "Referrals", icon: "ri-user-shared-line", stats: stats.referrals },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => {
              setTypeFilter(tab.key);
              setCurrentPage(1);
            }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
              typeFilter === tab.key
                ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg"
                : "bg-white border border-gray-200 text-gray-600 hover:border-blue-300 hover:text-blue-600"
            }`}
          >
            <i className={tab.icon}></i>
            <span>{tab.label}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs ${
              typeFilter === tab.key
                ? "bg-white/20 text-white"
                : "bg-gray-100 text-gray-600"
            }`}>
              {tab.stats.total}
            </span>
          </button>
        ))}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="ca-card p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <i className="ri-file-list-3-line text-2xl text-blue-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Total {typeFilter === "job" ? "Jobs" : typeFilter === "internship" ? "Internships" : "Referrals"}
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {currentStats.total}
              </p>
            </div>
          </div>
        </div>
        <div className="ca-card p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <i className="ri-checkbox-circle-line text-2xl text-green-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Active</p>
              <p className="text-2xl font-semibold text-gray-900">
                {currentStats.active}
              </p>
            </div>
          </div>
        </div>
        <div className="ca-card p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <i className="ri-time-line text-2xl text-red-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Expired</p>
              <p className="text-2xl font-semibold text-gray-900">
                {currentStats.total - currentStats.active}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters/Search */}
      <div className="ca-card p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <div className="relative">
              <input
                type="search"
                placeholder="Search by title or company..."
                className="ca-input pl-10 text-sm"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="ri-search-line text-gray-400"></i>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Status
            </label>
            <select
              className="ca-input text-sm"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Status</option>
              <option value="active">Active Only</option>
              <option value="expired">Expired Only</option>
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              type="button"
              className="ca-btn-secondary flex-1 px-4 py-2"
              onClick={clearFilters}
            >
              <i className="ri-refresh-line mr-2"></i>
              Reset
            </button>
            <button
              type="button"
              className="ca-btn-primary flex-1 px-4 py-2"
              onClick={() => {
                setCurrentPage(1);
                loadJobs();
              }}
            >
              <i className="ri-search-line mr-2"></i>
              Search
            </button>
          </div>
        </div>
      </div>

      {/* Job Table */}
      <div className="ca-card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            {typeFilter === "job" ? "Jobs" : typeFilter === "internship" ? "Internships" : "Referrals"} 
            <span className="text-gray-500 ml-2">({totalItems} total)</span>
          </h3>
          <Link
            to="/admin/add/new"
            className="ca-btn-primary inline-flex items-center px-4 py-2 text-sm"
          >
            <i className="ri-add-line mr-2"></i> Add New
          </Link>
        </div>

        {loading ? (
          <div className="p-6">
            <SkeletonAdminTable rows={10} columns={6} />
          </div>
        ) : sortedJobs.length === 0 ? (
          <div className="p-12 text-center">
            <i className="ri-briefcase-line text-5xl text-gray-300 mb-4"></i>
            <p className="text-gray-500">No {typeFilter}s found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort("title")}
                    >
                      <div className="flex items-center">
                        Title / Company
                        <SortIndicator field="title" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort("type")}
                    >
                      <div className="flex items-center">
                        Type
                        <SortIndicator field="type" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort("likes")}
                    >
                      <div className="flex items-center">
                        Stats
                        <SortIndicator field="likes" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort("expiryDate")}
                    >
                      <div className="flex items-center">
                        Expiry
                        <SortIndicator field="expiryDate" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedJobs.map((job) => {
                    const expired = isExpired(job);
                    const likesCount = job.likes
                      ? Object.keys(job.likes).length
                      : 0;
                    const appliesCount = job.applies
                      ? Object.keys(job.applies).length
                      : 0;

                    return (
                      <tr
                        key={job.id}
                        className={`hover:bg-gray-50 ${
                          expired ? "bg-red-50/50" : ""
                        }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <img
                              src={job.companyLogo}
                              alt={job.companyName}
                              className="w-10 h-10 rounded-lg object-contain bg-gray-100"
                              onError={(e) => {
                                e.target.src =
                                  "https://via.placeholder.com/40?text=" +
                                  (job.companyName?.charAt(0) || "C");
                              }}
                            />
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                                {job.title}
                              </div>
                              <div className="text-sm text-gray-500">
                                {job.companyName}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 capitalize">
                            {job.type}
                          </span>
                          <div className="text-xs text-gray-500 mt-1">
                            {job.experience?.join(", ") || "—"}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              expired
                                ? "bg-red-100 text-red-800"
                                : "bg-green-100 text-green-800"
                            }`}
                          >
                            {expired ? "Expired" : "Active"}
                          </span>
                          {job.isVerified && (
                            <span className="ml-2 px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              Verified
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          <div className="flex items-center gap-3">
                            <span
                              className="flex items-center gap-1"
                              title="Likes"
                            >
                              <i className="ri-heart-line text-red-500"></i>
                              {likesCount}
                            </span>
                            <span
                              className="flex items-center gap-1"
                              title="Applications"
                            >
                              <i className="ri-send-plane-line text-blue-500"></i>
                              {appliesCount}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(job.expiryDate)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                          <div className="flex items-center justify-center space-x-1">
                            <Link
                              to={`/jobs/${job.id}`}
                              className="inline-flex items-center px-2 py-1.5 rounded-lg text-gray-700 hover:bg-gray-100"
                              title="View"
                            >
                              <i className="ri-eye-line text-blue-600"></i>
                            </Link>
                            <button
                              onClick={() => setEditingJob(job)}
                              className="inline-flex items-center px-2 py-1.5 rounded-lg text-gray-700 hover:bg-gray-100"
                              title="Edit"
                            >
                              <i className="ri-edit-line text-green-600"></i>
                            </button>
                            <button
                              onClick={() => handleDelete(job.id)}
                              disabled={deletingJobId === job.id}
                              className="inline-flex items-center px-2 py-1.5 rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                              title="Delete"
                            >
                              {deletingJobId === job.id ? (
                                <i className="ri-loader-4-line animate-spin text-red-600"></i>
                              ) : (
                                <i className="ri-delete-bin-line text-red-600"></i>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="text-sm text-gray-500">
                  Showing page {currentPage} of {totalPages} ({totalItems} total)
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm disabled:opacity-50 hover:bg-gray-50 flex items-center gap-1"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                    Previous
                  </button>

                  {/* Page numbers */}
                  <div className="hidden sm:flex gap-1">
                    {(() => {
                      const pages = [];
                      const maxVisible = 5;

                      if (totalPages <= maxVisible) {
                        for (let i = 1; i <= totalPages; i++) pages.push(i);
                      } else if (currentPage <= 3) {
                        for (let i = 1; i <= 4; i++) pages.push(i);
                        pages.push("...");
                        pages.push(totalPages);
                      } else if (currentPage >= totalPages - 2) {
                        pages.push(1);
                        pages.push("...");
                        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
                      } else {
                        pages.push(1);
                        pages.push("...");
                        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
                        pages.push("...");
                        pages.push(totalPages);
                      }

                      return pages.map((page, idx) => {
                        if (page === "...") {
                          return (
                            <span
                              key={`ellipsis-${idx}`}
                              className="px-3 py-1.5 text-gray-400"
                            >
                              •••
                            </span>
                          );
                        }
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-1.5 rounded-lg text-sm ${
                              currentPage === page
                                ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
                                : "border border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {page}
                          </button>
                        );
                      });
                    })()}
                  </div>

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm disabled:opacity-50 hover:bg-gray-50 flex items-center gap-1"
                  >
                    Next
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Modal */}
      {editingJob && (
        <EditJobModal
          job={editingJob}
          onClose={() => setEditingJob(null)}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
}
